/**
 * queryClient.ts — [T246.5-FE-HOOKS]
 *
 * Single TanStack Query v5 `QueryClient` instance shared by every
 * widget-surface-preferences hook. Lives outside `main.tsx` so it can be
 * imported by tests, by the upcoming `InsightsGraphManager` page (Wave 6),
 * and by any cross-component invalidator — without coupling those
 * consumers to the React tree.
 *
 * Defaults rationale:
 *   • `staleTime: 60_000`          — global floor (1 minute). The
 *                                   widget-surface prefs GET hook bumps
 *                                   this to 5 minutes via
 *                                   `WIDGET_SURFACE_PREF_STALE_TIME_MS`,
 *                                   but a 1-minute default keeps unrelated
 *                                   queries from thrashing.
 *   • `gcTime: 5 * 60_000`         — 5 minutes of inactivity before GC.
 *                                   Matches the staleTime ceiling so a
 *                                   stale-then-GC'd query is rare.
 *   • `retry: 1`                   — one auto-retry on transient network
 *                                   errors. Mutations are NOT auto-retried
 *                                   (TanStack default) which is correct for
 *                                   write-side actions where double-send is
 *                                   worse than a single failure.
 *   • `refetchOnWindowFocus: false` — user-driven mutations invalidate
 *                                   explicitly; focus revalidation would
 *                                   race against the optimistic update.
 *
 * Provider tree wiring is intentionally NOT done here. The hook bundle
 * (`useWidgetSurfacePreferences`) only needs the client to be passed
 * to `QueryClientProvider` once at the app root — that wiring ships in
 * the Wave 6 PR alongside `InsightsGraphManager.tsx`. Until then, the
 * hooks are imported and used via the standard React Query call
 * surface, and a default client is provisioned on first hook invocation
 * so dev-only smoke testing works even without the provider.
 *
 * React Query v5 docs:
 *   https://tanstack.com/query/latest/docs/framework/react/reference/QueryClient
 *
 * @module lib/queryClient
 * @see .omo/plans/T246-PY-insights-graph-revamp-prd.md §13.4 (FE-HOOKS)
 */

import { QueryClient } from '@tanstack/react-query';
import type { QueryClientConfig } from '@tanstack/query-core';

// ── Default config ─────────────────────────────────────────────────────────

/**
 * Centralised defaults so both the singleton and the test-time builder
 * stay in lock-step. Tests can re-use this via `createAppQueryClient()`.
 *
 * Shape matches `QueryClientConfig.defaultOptions.{queries,mutations}` —
 * we DO NOT spread this directly into the `QueryClient` constructor
 * because TanStack Query v5 wraps defaults under `defaultOptions`.
 */
export const defaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
};

// ── Singleton ─────────────────────────────────────────────────────────────

/**
 * App-wide shared `QueryClient`. Import this from the provider tree and
 * pass it to `<QueryClientProvider client={appQueryClient}>`. Importing
 * it elsewhere is allowed but discouraged — call `createAppQueryClient()`
 * instead to avoid sharing the same cache across test harnesses.
 */
export const appQueryClient: QueryClient = new QueryClient(
  defaultQueryClientConfig
);

/**
 * Builder for test-time or alternate-context `QueryClient` instances.
 *
 * Use this when wiring tests (e.g. RTL render with `<QueryClientProvider>`)
 * or, in the future, when per-tenant sub-trees want isolated caches.
 *
 * Importantly, the returned client is configured identically to
 * `appQueryClient` — the React Query `queryKey` + invalidation contract
 * is preserved across instances, which keeps cross-component invalidations
 * (e.g. from `/widgets/insights` to `/transactions`) working as long as
 * the SAME prefix string is used by every instance.
 */
export function createAppQueryClient(
  overrides?: QueryClientConfig,
): QueryClient {
  return new QueryClient({
    ...defaultQueryClientConfig,
    ...overrides,
  });
}
